import { ConfigOption, FormlyFieldConfig } from '@ngx-formly/core';

import {
  FormlyButtonComponent,
  FormlyCheckboxComponent,
  FormlyDateComponent,
  FormlyArrayComponent,
  FormlyHorizontalWrapperComponent,
  FormlyFieldsComponent,
  FormlyImportButtonComponent,
  FormlyInlineWrapperComponent,
  FormlyInputComponent,
  FormlyInputSearchComponent,
  FormlyLineBreakComponent,
  FormlyRepeatComponent,
  FormlySelectComponent,
  FormlySummaryComponent,
  FormlyTemplateComponent,
  FormlyTextAreaComponent,
  FormlyFileUploadComponent,
  FormlyRadioComponent,
  FormlyAddressLookupComponent,
  FormlyWarpWeftPlanComponent,
  FormlyBackButtonComponent,
} from './components';

export const TmsFormlyConfig: ConfigOption = {
  types: [
    {
      name: 'input',
      component: FormlyInputComponent,
      wrappers: ['form-field'],
    },
    {
      name: 'input-search',
      component: FormlyInputSearchComponent,
      wrappers: ['form-field'],
    },
    {
      name: 'textarea',
      component: FormlyTextAreaComponent,
      wrappers: ['form-field'],
    },
    {
      name: 'date',
      component: FormlyDateComponent,
      wrappers: ['form-field'],
    },
    {
      name: 'select',
      component: FormlySelectComponent,
      wrappers: ['form-field'],
    },
    {
      name: 'checkbox',
      component: FormlyCheckboxComponent,
      wrappers: ['form-field'],
    },
    {
      name: 'radio',
      component: FormlyRadioComponent,
      wrappers: ['form-field'],
    },
    {
      component: FormlyFileUploadComponent,
      name: 'file-upload',
      wrappers: ['form-field'],
    },
    {
      component: FormlyAddressLookupComponent,
      name: 'address-lookup',
      wrappers: ['form-field'],
    },
    {
      component: FormlyButtonComponent,
      name: 'button',
      wrappers: ['form-field'],
    },
    {
      component: FormlyBackButtonComponent,
      name: 'back-button',
      wrappers: ['form-field'],
    },
    {
      component: FormlyImportButtonComponent,
      name: 'import-button',
      wrappers: ['form-field'],
    },
    {
      component: FormlyTemplateComponent,
      name: 'template',
      wrappers: ['form-field'],
    },
    {
      component: FormlyFieldsComponent,
      name: 'fields',
      wrappers: ['form-field'],
    },
    {
      component: FormlyWarpWeftPlanComponent,
      name: 'warp-weft-plan',
      wrappers: [],
    },
    {
      component: FormlyArrayComponent,
      name: 'array',
      wrappers: [],
    },
    {
      component: FormlySummaryComponent,
      name: 'summary',
      wrappers: [],
    },
    {
      component: FormlyRepeatComponent,
      name: 'repeat',
      wrappers: [],
    },
    {
      component: FormlyLineBreakComponent,
      name: 'line-break',
      wrappers: [],
    },
  ],
  wrappers: [
    { name: 'form-field', component: FormlyHorizontalWrapperComponent },
    { name: 'inline', component: FormlyInlineWrapperComponent },
  ],
  validationMessages: [
    { name: 'required', message: requiredValidationMessage },
    { name: 'minLength', message: minLengthValidationMessage },
    { name: 'maxLength', message: maxLengthValidationMessage },
    { name: 'min', message: minValidationMessage },
    { name: 'max', message: maxValidationMessage },
    { name: 'lessThan', message: lessThanValidationMessage },
    { name: 'greaterThan', message: greaterThanValidationMessage },
    { name: 'lessThanOrEqual', message: lessThanOrEqualValidationMessage },
    { name: 'greaterThanOrEqual', message: greaterThanOrEqualValidationMessage },
    { name: 'email', message: emailValidationMessage },
    { name: 'postcode', message: postcodeValidationMessage },
    { name: 'percentage', message: percentageValidationMessage },
    { name: 'integer', message: integerValidationMessage },
    { name: 'date', message: dateValidationMessage },
    { name: 'containsUppercase', message: containsUppercaseValidationMessage },
    { name: 'containsLowercase', message: containsLowercaseValidationMessage },
    { name: 'containsNumbers', message: containsNumbersValidationMessage },
    { name: 'containsNonAlphaNumeric', message: containsNonAlphaNumericValidationMessage },
    { name: 'notExists', message: notExistsValidationMessage },
  ],
};

export function requiredValidationMessage(error, field: FormlyFieldConfig): string {
  return `${getFieldName(field)} is required`;
}

export function minLengthValidationMessage(error, field: FormlyFieldConfig): string {
  return `${getFieldName(field)} should have at least ${error.requiredLength} characters`;
}

export function maxLengthValidationMessage(error, field: FormlyFieldConfig): string {
  return `${getFieldName(field)} should be less than ${error.requiredLength} characters`;
}

export function minValidationMessage(error, field: FormlyFieldConfig): string {
  return `${getFieldName(field)} should be a minimum of ${error.min}`;
}

export function maxValidationMessage(error, field: FormlyFieldConfig): string {
  return `${getFieldName(field)} should be a maximum of ${error.max}`;
}

export function lessThanValidationMessage(error, field: FormlyFieldConfig): string {
  return `${getFieldName(field)} should be less than ${field.formControl.errors.lessThan}`;
}

export function greaterThanValidationMessage(error, field: FormlyFieldConfig): string {
  return `${getFieldName(field)} should be greater than ${field.formControl.errors.greaterThan}`;
}

export function lessThanOrEqualValidationMessage(error, field: FormlyFieldConfig): string {
  return `${getFieldName(field)} should be a maximum of ${field.formControl.errors.lessThanOrEqual}`;
}

export function greaterThanOrEqualValidationMessage(error, field: FormlyFieldConfig): string {
  return `${getFieldName(field)} should be a minimum of ${field.formControl.errors.greaterThanOrEqual}`;
}

export function emailValidationMessage(error, field: FormlyFieldConfig): string {
  return `${getFieldName(field)} is not a valid email address`;
}

export function postcodeValidationMessage(error, field: FormlyFieldConfig): string {
  return `${getFieldName(field)} is not a valid postcode`;
}

export function percentageValidationMessage(error, field: FormlyFieldConfig): string {
  return `${getFieldName(field)} is not a valid percentage`;
}

export function integerValidationMessage(error, field: FormlyFieldConfig): string {
  return `${getFieldName(field)} must be a whole number`;
}

export function dateValidationMessage(error, field: FormlyFieldConfig): string {
  return `${getFieldName(field)} is not a valid date`;
}

export function containsUppercaseValidationMessage(error, field: FormlyFieldConfig): string {
  return `${getFieldName(field)} must contain at least one uppercase letter`;
}

export function containsLowercaseValidationMessage(error, field: FormlyFieldConfig): string {
  return `${getFieldName(field)} must contain at least one lowercase letter`;
}

export function containsNonAlphaNumericValidationMessage(error, field: FormlyFieldConfig): string {
  return `${getFieldName(field)} must contain at least one special character`;
}

export function containsNumbersValidationMessage(error, field: FormlyFieldConfig): string {
  return `${getFieldName(field)} must contain at least one number`;
}

export function notExistsValidationMessage(error, field: FormlyFieldConfig): string {
  return `${getFieldName(field)} already exists`;
}

export function getFieldName(field: FormlyFieldConfig): string {
  return field.props.label ?? field.key.toString();
}
